import { useCallback, useEffect, useRef } from "react";
import Sortable from "sortablejs";
import { toId } from "../../types/treeNode.js";
const CLASS_GETS_EMPTY = "aiot-sortable-gets-empty";
const useSortableTree = ({ enabled, treeId, sortableDelay, thresholdPx, forceSortableFallback, onSort, onSortStart, onSortEnd, onSortMove })=>{
    const sortablesRef = useRef({});
    const optionsRef = useRef({
        sortableDelay,
        thresholdPx,
        forceSortableFallback,
        onSort,
        onSortStart,
        onSortEnd,
        onSortMove
    });
    optionsRef.current = {
        sortableDelay,
        thresholdPx,
        forceSortableFallback,
        onSort,
        onSortStart,
        onSortEnd,
        onSortMove
    };
    const destroyAll = useCallback(()=>{
        Object.values(sortablesRef.current).forEach((sortable)=>sortable.destroy());
        sortablesRef.current = {};
    }, []);
    const handleSortableEnd = useCallback(async (evt)=>{
        const { from, to, oldIndex, newIndex, item } = evt;
        const parentFromId = toId(from.attributes["data-childs-for"].value);
        const parentToId = toId(to.attributes["data-childs-for"].value);
        from.classList.remove(CLASS_GETS_EMPTY);
        if (parentFromId === parentToId && oldIndex === newIndex) {
            document.body.classList.remove("aiot-currently-sorting");
            return;
        }
        const id = toId(item.attributes["data-li-id"].value);
        const nextSibling = item.nextElementSibling;
        const nextObj = nextSibling && nextSibling.attributes["data-li-id"] ? nextSibling : undefined;
        const prevObj = item.previousElementSibling;
        const nextId = nextObj && toId(nextObj.attributes["data-li-id"].value);
        const prevId = prevObj && toId(prevObj.attributes["data-li-id"].value);
        const { onSort: onSortHandler, onSortEnd: onSortEndHandler } = optionsRef.current;
        onSortEndHandler?.(evt);
        if (!onSortHandler) {
            throw new Error("You have to define a onSort function to enable sorting functionality.");
        }
        if (from !== to) {
            from.appendChild(item);
            item.style.display = "none";
            Object.values(sortablesRef.current).forEach((sortable)=>{
                const currentOrder = sortable._currentOrder;
                currentOrder && sortable.sort(currentOrder);
            });
        }
        try {
            await onSortHandler({
                evt,
                from,
                to,
                oldIndex,
                newIndex,
                id,
                nextObj,
                prevObj,
                nextId,
                prevId,
                parentFromId,
                parentToId,
                source: "drag"
            });
        } finally{
            document.body.classList.remove("aiot-currently-sorting");
        }
    }, []);
    const registerSortableUl = useCallback((ul, id = 0)=>{
        if (!enabled) {
            return;
        }
        const sortables = sortablesRef.current;
        if (!ul) {
            if (sortables[id]) {
                sortables[id].destroy();
                delete sortables[id];
            }
            return;
        }
        if (sortables[id]) {
            sortables[id].destroy();
            delete sortables[id];
        }
        const { sortableDelay: delay, onSortStart: onStart, onSortMove: onMove, forceSortableFallback, thresholdPx: touchThreshold } = optionsRef.current;
        sortables[id] = Sortable.create(ul, {
            group: `${treeId}--handleSortableTree`,
            sort: ul.className.indexOf("aiot-sortable-one") === -1,
            delay,
            filter: ".aiot-expander",
            touchStartThreshold: touchThreshold,
            fallbackTolerance: touchThreshold,
            forceFallback: !!forceSortableFallback,
            disabled: false,
            draggable: ".aiot-sortable",
            ghostClass: "aiot-sortable-ghost",
            chosenClass: "aiot-sortable-chosen",
            dragClass: "aiot-sortable-drag",
            dataIdAttr: "data-li-id",
            setData: (dataTransfer)=>{
                if (dataTransfer.setDragImage) {
                    const elem = document.createElement("div");
                    elem.style.display = "none";
                    document.body.appendChild(elem);
                    dataTransfer.setDragImage(elem, 0, 0);
                }
            },
            onMove,
            onStart: (evt)=>{
                Object.values(sortablesRef.current).forEach((sortable)=>{
                    sortable._currentOrder = sortable.toArray();
                });
                document.body.classList.add("aiot-currently-sorting");
                evt.from.classList.add(CLASS_GETS_EMPTY);
                onStart?.(evt);
            },
            onEnd: handleSortableEnd,
            animation: 100
        });
    }, [
        enabled,
        handleSortableEnd,
        treeId
    ]);
    useEffect(()=>{
        Object.values(sortablesRef.current).forEach((sortable)=>{
            sortable.option("delay", sortableDelay);
            sortable.option("touchStartThreshold", thresholdPx);
            sortable.option("fallbackTolerance", thresholdPx);
            sortable.option("forceFallback", !!forceSortableFallback);
        });
    }, [
        sortableDelay,
        thresholdPx,
        forceSortableFallback
    ]);
    useEffect(()=>()=>destroyAll(), [
        destroyAll
    ]);
    return {
        registerSortableUl,
        destroyAll
    };
};
export { useSortableTree };
